import { z } from "zod";

/**
 * Relationship status used on both Profile (existing clients) and Lead (prospects).
 * Same list, same semantics — admin sets this manually. No history is tracked.
 *
 * Internal codes are the persisted values. Hebrew labels are for display only —
 * change them freely without a data migration.
 */
export const ProfileLeadStatusSchema = z.enum([
	"LICENSE_SENT",
	"DOCS_FORWARDED_TO_DOCTOR",
	"WAITING_FOR_PATIENT_DOCS",
	"IN_DRUG_TRIAL",
	"HAS_LICENSE_NO_SERVICE_NEEDED",
	"NOT_RELEVANT",
	"RENEWAL_FOLLOWUP_NO_RESPONSE",
	"DO_NOT_SERVICE",
]);
export type TProfileLeadStatus = z.infer<typeof ProfileLeadStatusSchema>;

/** Hebrew display labels. Index by the persisted code. */
export const PROFILE_LEAD_STATUS_LABELS_HE: Record<TProfileLeadStatus, string> = {
	LICENSE_SENT: "נשלח רישיון",
	DOCS_FORWARDED_TO_DOCTOR: "הועברו מסמכים לרופא",
	WAITING_FOR_PATIENT_DOCS: "מחכים למסמכים ממטופל",
	IN_DRUG_TRIAL: "בתהליך מיצוי",
	HAS_LICENSE_NO_SERVICE_NEEDED: "יש רישיון, אך לא צריך שירות",
	NOT_RELEVANT: "לא רלוונטי",
	RENEWAL_FOLLOWUP_NO_RESPONSE: "קיבל פולאפ לחידוש, לא הגיב",
	DO_NOT_SERVICE: "לא לתת שירות יותר",
};

/** Convenience helper: code → Hebrew label, with safe fallback. */
export function getProfileLeadStatusLabel(
	code: TProfileLeadStatus | null | undefined,
): string {
	if (!code) return "";
	return PROFILE_LEAD_STATUS_LABELS_HE[code] ?? code;
}

/** Iterable list of all statuses with their labels — handy for dropdown options. */
export const PROFILE_LEAD_STATUS_OPTIONS: ReadonlyArray<{
	code: TProfileLeadStatus;
	label: string;
}> = ProfileLeadStatusSchema.options.map((code) => ({
	code,
	label: PROFILE_LEAD_STATUS_LABELS_HE[code],
}));
